import { useCallback, useEffect, useRef, useState } from 'react';
import { useServices } from '@/model';
import type { Message } from '@/model';
import { number, record } from './types';
import { TranscriptFacts } from './transcripts';

/** Ephemeral thread facts survive failed refreshes and superseded history pages. */
export function useMessageFacts(conversationId:string) {
  const services=useServices();
  const facts=useRef({removed:new Set<string>(),latest:new Map<string,Message>(),floor:0,transcripts:new TranscriptFacts()});
  const [,changed]=useState(0);
  useEffect(()=>services.sync.onUpdate(['message.edited','message.deleted','message.hidden','me.member_prefs','transcript.changed'],update=>{
    const data=record(update.data),message=record(data.message);
    if((data.conversation_id??message.conversation_id)!==conversationId)return;
    const state=facts.current;
    if(update.type==='transcript.changed'&&typeof data.media_id==='string'&&typeof data.message_id==='string')state.transcripts.rememberValue(data.media_id,data.message_id,data.transcript);
    if(update.type==='message.hidden')for(const id of Array.isArray(data.message_ids)?data.message_ids:[])if(typeof id==='string'){state.removed.add(id);state.latest.delete(id);}
    if(update.type==='me.member_prefs')state.floor=Math.max(state.floor,number(data.cleared_seq));
    if(typeof message.id==='string'){
      if(update.type==='message.deleted'||message.hidden===true||message.deleted_at){state.removed.add(message.id);state.latest.delete(message.id);}
      else if(!state.removed.has(message.id))state.latest.set(message.id,state.transcripts.merge(state.latest.get(message.id),data.message as Message));
    }
    changed(value=>value+1);
  }),[services,conversationId]);
  return useCallback((messages:readonly Message[]):Message[]=>{
    const state=facts.current,result=new Map<string,Message>();
    for(const message of messages){
      if(message.hidden===true||message.deleted_at){state.removed.add(message.id);state.latest.delete(message.id);}
      if(state.removed.has(message.id)||message.seq<=state.floor)continue;
      const prior=state.latest.get(message.id);
      const current=state.transcripts.merge(prior,message);
      state.latest.set(message.id,current);result.set(message.id,current);
    }
    return [...result.values()];
  },[]);
}
